import { ClientTypeContext, EXCLUDE_RESTRICTED_CLIENT_TYPES, INERT_CLIENT_TYPES, toClientTypeContext } from "../src/taskpane/components/helpers/dataSourceFieldHelper";
import { ClientTypeLookup } from "../src/taskpane/services/clientTypeQuery";
import { SharePointSiteHelper } from "../utilities/SharePointSiteHelper";

/**
 * Advanced Search evaluates client type in SINGLE-SITE-COLLECTION mode only.
 *
 * Scope is measured in DISTINCT extracted site-collection GUIDs, not entries: selecting a
 * main site auto-selects its subsites and channel sites, so the everyday selection is several
 * entries but one collection - and every web of a collection shares the same client row
 * (client type is a property of the site collection). Counting entries would silently
 * downgrade every restricted datasource to a text box on exactly the sites this feature
 * targets.
 *
 * - 0 entries        - inert. Load-bearing, not a nicety: excludeRestricted mode would
 *                      otherwise disagree with the render path, which receives an empty site
 *                      array and treats every dimension as inert. The two paths must land in
 *                      the same mode.
 * - 1 collection     - the collection's resolved type, or inert when the lookup could not
 *                      answer. (A single unextractable entry also lands here: its lookup is
 *                      unknown, so the dimension is inert.)
 * - 2+ collections,  - a per-location rule has no correct answer across a set of locations.
 *   or unprovable      Allow-if-any over-exposes silently; require-all silently strips
 *                      fields; a scope containing an entry that cannot be dissected cannot be
 *                      PROVEN single. Declining is visible and recoverable: the user narrows
 *                      the selection and the picker returns.
 */
export const selectClientTypeMode = (compositeSiteIds: (string | undefined)[], lookup?: ClientTypeLookup): ClientTypeContext => {
  const entries: (string | undefined)[] = compositeSiteIds ?? [];
  if (entries.length === 0) {
    return INERT_CLIENT_TYPES;
  }
  if (entries.length === 1) {
    return toClientTypeContext(lookup);
  }
  const collections: (string | undefined)[] = entries.map(id => SharePointSiteHelper.extractSharePointSiteId(id));
  if (collections.every(Boolean) && new Set(collections).size === 1) {
    return toClientTypeContext(lookup);
  }
  // Singleton, not a fresh literal: this runs per render in Advanced Search, and a fresh
  // object would re-fire every [clientTypes]-keyed memo in every input on every render.
  return EXCLUDE_RESTRICTED_CLIENT_TYPES;
};
